import { Reducer } from 'redux';

import { reducer } from '../routes/index';
import { combineReducersCustom } from '../service/Redux/Redux';
import { GridInitialData } from '../typings/grid';
import { LogInitialData } from '../typings/itemsListTextareaEditorState';
import { State } from '../typings/state';
import { videoCreator } from '../video-creator/common/reducers';
import { imageUploaderReducer } from '../video-creator/VideoCreatorImageUploader/reducer';

import { addChatListEditor } from './addChatListEditor/addChatListEditor';
import { bannerCreativePreviewPopupReducer } from './bannerCreativePreviewPopup';
import { bannersEditMassActions } from './banners-edit/banners-edit-mass-actions/banners-edit-mass-actions';
import { calloutsReducer } from './banners-edit/callouts';
import { pricesReducer } from './banners-edit/prices';
import { bannerSiteLinksEditorReducer } from './banners-edit/sitelinks';
import { bannersMultiEdit as editBanners } from './bannersEdit';
import { internalBannersMultiEdit as editInternalBanners } from './bannersEdit/internal';
import { bannersPreviewData } from './bannersPreviewData';
import { campaignPlaceEditor } from './campaignPlaceEditor';
import { campaignsEdit as editCampaigns } from './campaignsEdit';
import { campaignServiceEditor } from './campaignServiceEditor';
import { canvasEditorReducer } from './canvasEditor';
import { chatListEditorReducer } from './chatListEditor/chatListEditor';
import { columnsSettingsEditor } from './columnsSettingsEditor';
import { CommonInitializationData, getCommonData } from './common';
import { commonPriceEditor } from './commonPriceEditor';
import { creative } from './creative';
import { datesIntervalEditor } from './datesIntervalEditor';
import { deal as deals } from './deals';
import { devInfo } from './devInfo';
import { editPageItemNameEditor } from './editPageItemName/editPageItemName';
import { experimentsEditor } from './experimentsEditor';
import { feedFiltersRootReducer as feedFilters } from './feed-filters';
import { freelancers } from './freelancers';
import { goalsEdit } from './goals';
import { getGrid } from './grid';
import { groupsEditMassActions } from './groups-edit/groups-edit-mass-actions/groups-edit-mass-actions';
import { groupsMultiEdit as editGroups } from './groupsEdit';
import { getHeader, HeaderInitialData } from './header';
import { help } from './help';
import { imageEditorReducer } from './imageEditor';
import { imageSmartCenterEditor } from './imageSmartCenterEditor';
import { importExportWizard } from './importExportWizard';
import { importWizard } from './importWizard';
import { importWizardInfo } from './importWizardInfo/internal';
import { indoorMapEditor } from './indoorMapEditor';
import { itemsListTextareaEditor } from './itemsListTextareaEditor/itemsListTextareaEditor';
import { keywordsEditorReducer } from './keywordsEditor';
import { log } from './log';
import { makePricesWizardReducer } from './make-prices-wizard-reducer';
import { internalMarketer } from './marketer/internal';
import { internalMarketers } from './marketers/internal';
import { messages } from './messages';
import { metrikaCounterEditor } from './metrikaCounterEditor';
import { metrikaKeyGoalsEditor } from './metrikaKeyGoals';
import { minusPhraseLibrary } from './minus-phrase-library';
import { minusPhraseLibraryEditor } from './minus-phrase-library-editor';
import { mobileAppsCreate } from './mobile-app';
import { mobileAppsList } from './mobile-apps-list';
import { notificationEditor } from './notificationEditor';
import { organizationsData } from './organizationsData';
import { organizationsEditor } from './organizationsEditor';
import { outdoorMapEditor } from './outdoorMapEditor';
import { phrasesSelectorsReducer } from './phrases-selector';
import { phrasesSelectorWizard } from './phrasesSelectorWizard';
import { piTagsEditor } from './piTagsEditor';
import { internalProductPlaces } from './places/internal';
import { platformsPreviewEditor } from './platformsPreviewEditor';
import { predictor } from './predictor';
import { predictorEditor } from './predictorEditor';
import { pricePackagesReducer as pricePackages } from './pricePackages';
import { pricePackagesClientsModalReducer } from './pricePackagesClientsModal';
import { pricePackagesEditReducer as editPricePackages } from './pricePackagesEdit';
import { internalProducts } from './products/internal';
import { rateCorrectionsEditor } from './rateCorrectionsEditor';
import { recommendationModalReducer } from './recommendationModal';
import { recommendations } from './recommendations';
import { recommendationsGrid } from './recommendationsGrid';
import { regions } from './regions';
import { regionsEditor } from './regionsEditor';
import { regionsSearchEditor } from './regionsSearchEditor';
import { regionsTransformEditor } from './regionsTransformEditor';
import { relevantPhrasesEditor } from './relevantPhrases';
import { reportClient } from './reportsClient';
import { reportDomain } from './reportsDomain';
import { retargetingConditionEditor } from './retargetingConditionEditor';
import { retargetingEditor } from './retargetingEditor';
import { withRouteFlow } from './routeFlow';
import { showsFrequencyEditor } from './showsFrequency';
import { strategyEdiorReducer } from './strategyEditor';
import { tagCloudTargeting } from './tagCloudTargeting';
import { audienceTargetingsEditor } from './targetings/audience';
import { cryptaAudioGenresEditor } from './targetings/cryptaAudioGenres';
import { cryptaBehavioralEditor } from './targetings/cryptaBehavioral';
import { cryptaGroupFamilyEditor } from './targetings/cryptaGroupFamily';
import { cryptaSocialDemoEditor } from './targetings/cryptaSocialDemo';
import { interestsTargetingEditor } from './targetings/interestsEditor';
import { retargetingAudienceEditor } from './targetings/retargetingAudienceEditor';
import { targetTagsEditor } from './targetTagsEditor';
import { timeTargetingEditor } from './timeTargetingEditor';
import { trackingParametersEditor } from './trackingParametersEditor';
import { turbolandings } from './turbolandings';
import { userSettings } from './userSettings/userSettings';
import { video } from './video';
import { visitingCardEditor } from './visitingCardEditor';
import { getWelcome, WelcomeInitializationData } from './welcome';

export interface InitializationData {
    commonData: CommonInitializationData;
    grid: GridInitialData;
    header: HeaderInitialData;
    welcome?: WelcomeInitializationData;
    log?: LogInitialData;
}

export function getRootReducer(initData: InitializationData): Reducer<State> {
    const combinedReducer: Reducer<State> = combineReducersCustom(
        withRouteFlow({
            commonData: getCommonData(initData.commonData),
            userSettings,
            welcome: getWelcome(initData.welcome),
            imageSmartCenterEditor,
            grid: getGrid(initData.grid),
            header: getHeader(initData.header),
            creative,
            log,
            reportClient,
            reportDomain,
            phrasesSelectorWizard,
            phrasesSelectors: phrasesSelectorsReducer,
            router: reducer,
            mobileAppsList,
            mobileAppsCreate,
            recommendationsGrid,
            recommendations,
            calloutsEditor: calloutsReducer,
            help,
            siteLinksEditor: bannerSiteLinksEditorReducer,
            bannersPriceEditor: pricesReducer,
            video,
            editBanners,
            bannersEditMassActions,
            ...freelancers,
            deals,
            editCampaigns,
            turbolandings,
            editInternalBanners,
            editGroups,
            regions,
            bannersPreviewData,
            devInfo,
            audienceTargetingsEditor,
            rateCorrectionsEditor,
            interestsTargetingEditor,
            cryptaSocialDemoEditor,
            cryptaGroupFamilyEditor,
            cryptaBehavioralEditor,
            cryptaAudioGenresEditor,
            retargetingEditor,
            retargetingConditionEditor,
            outdoorMapEditor,
            indoorMapEditor,
            platformsPreviewEditor,
            regionsEditor,
            regionsTransformEditor,
            minusPhraseLibrary,
            predictor,
            columnsSettingsEditor,
            goalsData: goalsEdit,
            feedFilters,
            groupsEditMassActions,
            minusPhraseLibraryEditor,
            commonPriceEditor,
            chatListEditor: chatListEditorReducer,
            addChatListEditor,
            metrikaCounterEditor,
            relevantPhrasesEditor,
            imageEditor: imageEditorReducer,
            datesIntervalEditor,
            editPageItemNameEditor,
            bannerCreativePreviewPopup: bannerCreativePreviewPopupReducer,
            recommendationsModal: recommendationModalReducer,
            itemsListTextareaEditor,
            metrikaKeyGoalsEditor,
            notificationEditor,
            visitingCardEditor,
            experimentsEditor,
            trackingParametersState: trackingParametersEditor,
            internalProductPlaces: internalProductPlaces,
            products: internalProducts,
            importWizardInfo: importWizardInfo,
            marketerCard: internalMarketer,
            campaignServiceEditor: campaignServiceEditor,
            campaignPlaceEditor: campaignPlaceEditor,
            marketersList: internalMarketers,
            organizationsEditor,
            organizationsData,
            importExportWizard,
            importWizard,
            strategyEditor: strategyEdiorReducer,
            timeTargetingEditor,
            tagCloudTargetingEditor: tagCloudTargeting,
            predictorEditor,
            retargetingAudienceEditor,
            keywordsEditor: keywordsEditorReducer,
            pricePackages,
            editPricePackages,
            pricePackagesClientsModal: pricePackagesClientsModalReducer,
            piTagsEditor,
            canvasEditor: canvasEditorReducer,
            regionsSearchEditor,
            showsFrequencyEditor,
            targetTagsEditor,
            messages,
            ...videoCreator,
            imageUploaderEditor: imageUploaderReducer
        })
    );

    const pricesWizardReducer = makePricesWizardReducer(initData.grid);

    return (state, action): State => {
        const newState = combinedReducer(state, action);

        return pricesWizardReducer(newState, action, state);
    };
}
